import * as THREE from "three";
import {
	getTerrainSlopeAtPoint,
	type SiteConditions,
} from "../../domain/foundation/siteConditions";
import { getGroundReliefAt } from "./SceneEnvironment.helpers";
import type { ResolvedViewerQualityTier } from "./viewerConfig";

export const FOREST_BUDGET = {
	low: 36,
	balanced: 66,
	high: 90,
	textures: 3,
	// Three species batches, the distant ground apron, and two bark-trunk
	// batches (conifer, birch).
	drawables: 6,
} as const;

/**
 * The sprites' painted trunks stand at x≈240 of 512 px (measured in all three
 * PNGs just above the root flare), not the texture centre. The cross planes
 * shift their UVs so the painted trunk lines up with the modelled trunk on
 * the tree's axis.
 */
export const FOREST_SPRITE_TRUNK_U = 240 / 512;

/**
 * Height (as a fraction of the sprite) below which the painted trunk is
 * replaced by the modelled one: the open spruce shows its trunk to ~0.2,
 * the dense spruce only at the very foot, the birch up into the crown.
 */
export const FOREST_SPRITE_TRUNK_TOP = [0.22, 0.12, 0.56] as const;

/** Trunk segments: 10 around × 8 up keeps the whole high forest ≈ 14k tris. */
const TRUNK_RADIAL = 10;
const TRUNK_RINGS = 8;
export const FOREST_TEXTURES = [
	"/assets/forest/spruce-open.png",
	"/assets/forest/spruce-dense.png",
	"/assets/forest/birch.png",
] as const;

function random(index: number, salt: number) {
	let h = Math.imul(index + 1, 374761393) ^ Math.imul(salt + 1, 668265263);
	h = Math.imul(h ^ (h >>> 13), 1274126177);
	return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

export function buildForestInstances(
	tier: ResolvedViewerQualityTier,
	groundY: number,
	site: SiteConditions,
) {
	return Array.from({ length: FOREST_BUDGET[tier] }, (_, i) => {
		// Forest belongs on the land behind the sauna (-Z). Leave the lake
		// and its far shore (+Z) open, including the full tree crown widths.
		// Prefix-stable distribution: quality changes add trees without moving them.
		const angle =
			Math.PI +
			((((i * 0.618033989 + random(i, 0) * 0.08) % 1) * 2 - 1) * Math.PI) / 3;
		const radius = 17 + (i % 3) * 7 + random(i, 1) * 2.5;
		const x = Math.sin(angle) * radius;
		const z = Math.cos(angle) * radius;
		const species = i % 7 === 0 ? 2 : i % 2;
		const height = species === 2 ? 7 + random(i, 3) * 3 : 8 + random(i, 3) * 6;
		const width = height * (species === 2 ? 0.82 : 0.76 + random(i, 4) * 0.16);
		// Alpha-tested sprites include 11/22 pixels below the visible roots.
		const rootPadding = (height * (species === 2 ? 22 : 11)) / 512;
		const ground =
			groundY + getTerrainSlopeAtPoint(site, x, z) + getGroundReliefAt(x, z);
		const y = ground - rootPadding - 0.12;
		const yaw = random(i, 5) * Math.PI;
		// Modelled trunk: wider than the painted one (≈0.016 H half-width) so it
		// hides it from every angle, and tall enough to vanish into the crown.
		const trunkRadius = height * (species === 2 ? 0.02 : 0.024);
		const trunkHeight = height * (species === 2 ? 0.72 : 0.62);
		return {
			species,
			radius,
			height,
			width,
			trunk: new THREE.Matrix4().compose(
				new THREE.Vector3(x, ground - 0.08, z),
				new THREE.Quaternion().setFromAxisAngle(
					new THREE.Vector3(0, 1, 0),
					yaw + random(i, 7) * 2,
				),
				new THREE.Vector3(trunkRadius, trunkHeight, trunkRadius),
			),
			matrix: new THREE.Matrix4().compose(
				new THREE.Vector3(x, y, z),
				new THREE.Quaternion().setFromAxisAngle(
					new THREE.Vector3(0, 1, 0),
					yaw,
				),
				new THREE.Vector3(width, height, width),
			),
			// The map owns the foliage colour. This is only a subtle neutral gain.
			gain: 0.86 + random(i, 6) * 0.24,
		};
	});
}

/** Three fixed crossing planes: no per-frame billboarding or camera listener. */
export function createForestCrossGeometry() {
	const positions: number[] = [],
		uv: number[] = [],
		indices: number[] = [];
	for (let side = 0; side < 3; side++) {
		const angle = (side * Math.PI) / 3;
		const x = Math.cos(angle) * 0.5,
			z = Math.sin(angle) * 0.5;
		const start = positions.length / 3;
		positions.push(-x, 0, -z, x, 0, z, x, 1, z, -x, 1, -z);
		const u0 = FOREST_SPRITE_TRUNK_U - 0.5;
		uv.push(u0, 0, u0 + 1, 0, u0 + 1, 1, u0, 1);
		indices.push(start, start + 1, start + 2, start, start + 2, start + 3);
	}
	const geometry = new THREE.BufferGeometry();
	geometry.setAttribute(
		"position",
		new THREE.Float32BufferAttribute(positions, 3),
	);
	geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
	geometry.setIndex(indices);
	geometry.computeVertexNormals();
	geometry.computeBoundingSphere();
	return geometry;
}

/** Extend only the land behind the sauna; no far-shore apron behind the lake. */
export function createForestFloor(groundY: number, site: SiteConditions) {
	const positions: number[] = [],
		uv: number[] = [],
		indices: number[] = [];
	for (const radius of [15, 42]) {
		for (const [x, z] of [
			[-radius, -radius],
			[radius, -radius],
			[radius, radius],
			[-radius, radius],
		]) {
			const rearZ = Math.min(z, -6);
			positions.push(
				x,
				groundY + getTerrainSlopeAtPoint(site, x, rearZ) - 0.025,
				rearZ,
			);
			uv.push(x / 30 + 0.5, -rearZ / 30 + 0.5);
		}
	}
	for (const i of [0, 1, 3]) {
		const j = (i + 1) % 4;
		indices.push(i, j, j + 4, i, j + 4, i + 4);
	}
	const geometry = new THREE.BufferGeometry();
	geometry.setAttribute(
		"position",
		new THREE.Float32BufferAttribute(positions, 3),
	);
	geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
	geometry.setIndex(indices);
	geometry.computeVertexNormals();
	geometry.computeBoundingSphere();
	return geometry;
}

/**
 * A unit trunk (radius 1 at the foot above the flare, height 1): a tapering
 * column that thins to a quarter at the top (hidden in the crown) and a
 * root flare of five buttresses over the lowest tenth. Scaled per tree.
 */
export function createTrunkGeometry() {
	const positions: number[] = [],
		uv: number[] = [],
		indices: number[] = [];
	const cols = TRUNK_RADIAL + 1;
	for (let ring = 0; ring <= TRUNK_RINGS; ring++) {
		// Rings crowd toward the foot, where the flare needs the resolution.
		const t = (ring / TRUNK_RINGS) ** 1.8;
		const taper = 1 - 0.75 * t;
		const flare = Math.max(0, 1 - t / 0.1) ** 2;
		for (let side = 0; side <= TRUNK_RADIAL; side++) {
			const a = (side / TRUNK_RADIAL) * Math.PI * 2;
			const buttress = 0.5 + 0.5 * Math.cos(a * 5);
			const r = taper * (1 + flare * (0.35 + 0.9 * buttress * buttress));
			positions.push(Math.cos(a) * r, t, Math.sin(a) * r);
			uv.push(side / TRUNK_RADIAL, t);
		}
	}
	for (let ring = 0; ring < TRUNK_RINGS; ring++) {
		for (let side = 0; side < TRUNK_RADIAL; side++) {
			const a = ring * cols + side;
			const b = a + 1;
			const c = a + cols;
			const d = c + 1;
			indices.push(a, c, b, b, c, d);
		}
	}
	const geometry = new THREE.BufferGeometry();
	geometry.setAttribute(
		"position",
		new THREE.Float32BufferAttribute(positions, 3),
	);
	geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
	geometry.setIndex(indices);
	geometry.computeVertexNormals();
	geometry.computeBoundingSphere();
	return geometry;
}
